import { getLocalTimeZone, today } from "@internationalized/date";
import { UseQueryResult } from "@tanstack/react-query";
import { DateTimeRangePicker } from "features/CenterContent/RoleContent/ProductionDeviation/components/DateTimeRangePicker";
import { ShowTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/components/ShowTrainSeries";
import { DerivedTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/steps/DerivedTrainSeries";
import {
  makeBatchProps,
  Path,
  useProductionDeviationEffectivePath,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FormInputs } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/batchSections/FormInputs";
import { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { Alert, Flex, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

type ProductionDeviationFormInputsProps = {
  path: Path;
  isEditMode: boolean;
  onClose: () => void;
  onSubmit: () => void;
  opInfoTypes: UseQueryResult<OperationalInformationTypes>;
  trainSeries: string[];
  setTrainSeries: Dispatch<SetStateAction<string[]>>;
  includeAllTrainSeries: boolean;
  setIncludeAllTrainSeries: Dispatch<SetStateAction<boolean>>;
  trigger: () => Promise<boolean>;
};

export const ProductionDeviationFormInputs: FC<
  ProductionDeviationFormInputsProps
> = ({
  path,
  isEditMode,
  onClose,
  onSubmit,
  opInfoTypes,
  trainSeries,
  setTrainSeries,
  includeAllTrainSeries,
  setIncludeAllTrainSeries,
  trigger,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, status } = opInfoTypes;
  const hasTrainSeries = trainSeries.length > 0;

  const effectivePath = useProductionDeviationEffectivePath(
    path,
    isEditMode,
    trainSeries,
  );

  const mode = isEditMode ? "edit" : "create";

  return (
    <ActionModal
      title={`${isEditMode ? t("Endre") : t("Opprett")} ${t("produksjonsavvik")}`}
      actionTitle={`${t("Neste")} →`}
      onClose={onClose}
      isSubmitDisabled={!isEditMode && !hasTrainSeries}
      onSubmit={async () => {
        const success = await trigger();
        if (!success) return;
        onSubmit();
      }}
      loading={false}
      isSuccess={false}
      isError={false}
      successMessage=""
      failureMessage=""
      subHeaders={
        isEditMode ? [<ShowTrainSeries trainSeries={trainSeries} />] : undefined
      }
    >
      <Flex flexDir="column" gap="6px" w="100%" mb="12px">
        {isEditMode && (
          <Alert data-color="info" width="100%">
            {t("Opprett nytt avvik for å endre periode eller hendelsetype.")}
          </Alert>
        )}
        <Flex flexDir="column" gap="6px">
          <Text
            variant="sm"
            fontWeight={700}
            marginTop={isEditMode ? "9px" : undefined}
            color={isEditMode ? theme.colorTextDisabled : theme.colorTextMain}
          >
            {t("Tidsperiode")}
          </Text>
          <DateTimeRangePicker
            durationsField="durations"
            minValue={isEditMode ? undefined : today(getLocalTimeZone())}
            maxValue={
              isEditMode
                ? undefined
                : today(getLocalTimeZone()).add({ days: 13 })
            }
            isDisabled={isEditMode}
          />
        </Flex>
      </Flex>
      {!isEditMode && (
        <Flex w="100%" mb="12px">
          <DerivedTrainSeries
            trainSeries={trainSeries}
            setTrainSeries={setTrainSeries}
            includeAll={includeAllTrainSeries}
            setIncludeAll={setIncludeAllTrainSeries}
          />
        </Flex>
      )}
      <Flex w="100%">
        {status === "pending" && (
          <SkeletonLoader skeletonType="trainInfoForm" />
        )}
        {status === "error" && <FailureMessage flex={1} m="0px" maxW="100%" />}
        {status === "success" && (
          <FormInputs {...makeBatchProps(effectivePath, data, mode)} />
        )}
      </Flex>
    </ActionModal>
  );
};
